import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react'

type Variant = 'idle' | 'link' | 'label' | 'hidden'

const INTERACTIVE = '[data-cursor], a, button, [role="button"], summary, label, input, textarea, select'

// A precise dot that always sits exactly on the pointer, plus a trailing ring
// that grows over interactive elements and carries a short label when an
// element asks for one with data-cursor.
export const Cursor: FC = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const [variant, setVariant] = useState<Variant>('hidden')
    const [label, setLabel] = useState('')
    const x = useMotionValue(-100)
    const y = useMotionValue(-100)
    const ringX = useSpring(x, { stiffness: 420, damping: 38, mass: 0.6 })
    const ringY = useSpring(y, { stiffness: 420, damping: 38, mass: 0.6 })

    useEffect(() => {
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (!fine || reduce) return

        setEnabled(true)
        document.documentElement.classList.add('has-custom-cursor')

        const onMove = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return

            x.set(event.clientX)
            y.set(event.clientY)
        }

        const onOver = (event: PointerEvent) => {
            const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(INTERACTIVE)

            if (!target) {
                setVariant('idle')
                return
            }

            if (target.matches('input, textarea, select')) {
                setVariant('hidden')
                return
            }

            const text = target.dataset.cursor

            if (text) {
                setLabel(text)
                setVariant('label')
                return
            }

            setVariant('link')
        }

        const onLeave = () => setVariant('hidden')
        const onEnter = () => setVariant('idle')

        window.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('pointerover', onOver, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)
        document.documentElement.addEventListener('pointerenter', onEnter)

        return () => {
            window.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerover', onOver)
            document.documentElement.removeEventListener('pointerleave', onLeave)
            document.documentElement.removeEventListener('pointerenter', onEnter)
            document.documentElement.classList.remove('has-custom-cursor')
        }
    }, [x, y])

    if (!enabled) return null

    const ringScale = variant === 'link' ? 1.9 : variant === 'idle' ? 1 : 0

    return (
        <div aria-hidden className='pointer-events-none fixed inset-0 z-[100]'>
            <div className='absolute inset-0 mix-blend-difference'>
                <motion.div
                    className='absolute left-0 top-0 size-9 -translate-x-1/2 -translate-y-1/2'
                    style={{ x: ringX, y: ringY }}
                >
                    <motion.div
                        className='size-full rounded-full border border-white'
                        animate={{
                            scale: ringScale,
                            backgroundColor: variant === 'link' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0)',
                            opacity: variant === 'hidden' ? 0 : 1
                        }}
                        transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                    />
                </motion.div>

                <motion.div
                    className='absolute left-0 top-0 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white'
                    style={{ x, y }}
                    animate={{ opacity: variant === 'hidden' || variant === 'label' ? 0 : 1 }}
                    transition={{ duration: 0.15 }}
                />
            </div>

            <motion.div
                className='absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2'
                style={{ x: ringX, y: ringY }}
            >
                <AnimatePresence>
                    {variant === 'label' && (
                        <motion.div
                            key='label'
                            className='flex size-24 items-center justify-center rounded-full bg-violet text-center font-mono text-[0.625rem] uppercase leading-tight tracking-[0.16em] text-white shadow-[0_0_60px_rgba(140,95,255,0.45)]'
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                        >
                            <span className='px-3'>
                                {label}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    )
}
